// @@COLLECTIONS_v123@@ Collections — clients with past-due Zoho invoices. Send a past-due notice
// (reminder / finance charge / collections wording, picked contacts, preview) or create this month's
// 1.5% finance charge as a DRAFT invoice in Zoho. Admin/manager only (server-gated).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import PortalPayments from '../components/PortalPayments'; // @@PORTAL_PAY_v124@@

const FILTERS = [
  { k: 'over30', label: 'Over 30 days' },
  { k: 'all', label: 'All past due' },
  { k: '1_30', label: '1–30' },
  { k: '31_60', label: '31–60' },
  { k: '61_90', label: '61–90' },
  { k: '91_plus', label: '90+' },
];
const LEVEL_LABEL = { reminder: 'Reminder', late_fee: 'Late-fee notice', collections: 'Collections warning' };
const STAGE_LABEL = { reminder: 'Past due', late_fee: 'Late fee', collections: 'Collections' };
const LEVEL_HELP = {
  reminder: 'Friendly reminder — finance charges start after 30 days past due.',
  late_fee: 'States that the 1.5%/month finance charge now applies.',
  collections: 'Adds the 90-day collections and collection-costs warning.',
};
const NOTE_MAX = 2000;
const wrapDoc = (html) => '<!doctype html><html><body style="margin:16px;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#1a1a1a;background:#ffffff">' + html + '</body></html>';
const hasEmail = (c) => !!(c && c.email && String(c.email).trim());
const nameOf = (c) => (`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Unnamed contact');

function money(n, ccy) {
  if (n == null) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy || 'USD' }).format(n); }
  catch (e) { return '$' + Number(n).toFixed(2); }
}
// YYYY-MM-DD by string parts — never new Date(iso) (Phoenix shows the day before).
function day(s) {
  if (!s) return '—';
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y) return '—';
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
}
function stamp(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch (e) { return ''; }
}
function matches(c, f) {
  if (f === 'all') return true;
  if (f === 'over30') return c.oldest_days > 30;
  return c.bucket === f;
}
async function readJson(r) { return r.json().catch(() => ({})); }

function Stage({ level }) {
  return <span className={'hq-pill hq-v123-lv hq-v123-lv-' + level}>{STAGE_LABEL[level] || level}</span>;
}

function Detail({ c }) {
  const [h, setH] = useState(null);
  useEffect(() => {
    if (!c.client_id) return undefined;
    let on = true;
    apiFetch('/api/collections/' + c.client_id + '/history')
      .then((r) => (r.ok ? r.json() : null)).then((j) => { if (on) setH(j || { notices: [], charges: [] }); })
      .catch(() => { if (on) setH({ notices: [], charges: [] }); });
    return () => { on = false; };
  }, [c.client_id]);
  return (
    <div className="hq-v123-detail">
      <div className="hq-v123-inv">
        <table>
          <thead><tr><th>Invoice #</th><th>Due</th><th className="r">Days past due</th><th className="r">Balance</th></tr></thead>
          <tbody>
            {c.invoices.map((i) => (
              <tr key={i.number}>
                <td>{i.number}{i.reference && /^finance charge/i.test(i.reference) ? <span className="hq-v123-muted"> · finance charge</span> : null}</td>
                <td>{day(i.due_date)}</td>
                <td className={'r' + (i.days_past_due > 90 ? ' hq-v123-red' : i.days_past_due > 30 ? ' hq-v123-amber' : '')}>{i.days_past_due}</td>
                <td className="r">{money(i.balance, c.currency_code)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="hq-v123-hist">
        {!c.client_id ? (
          <p className="hq-v123-muted">This Zoho customer isn’t linked to a client in HQ, so there are no contacts to notify. Link it from the client’s page (Zoho customer) to enable notices and finance charges.</p>
        ) : !h ? <p className="hq-v123-muted">Loading history…</p> : (
          <>
            <span className="hq-v123-k">Notices sent</span>
            {h.notices.length === 0 ? <p className="hq-v123-muted">None yet.</p> : (
              <ul>{h.notices.map((n) => (
                <li key={n.id}><b>{LEVEL_LABEL[n.level] || n.level}</b> · {stamp(n.at)} · {n.invoice_count} invoice{n.invoice_count === 1 ? '' : 's'}, {money(n.overdue_total)} · {n.sent}/{n.recipients} sent{n.by ? ' · ' + n.by : ''}</li>
              ))}</ul>
            )}
            <span className="hq-v123-k">Finance charges</span>
            {h.charges.length === 0 ? <p className="hq-v123-muted">None yet.</p> : (
              <ul>{h.charges.map((f, i) => (
                <li key={i}>{f.month} · {money(f.amount)} on {f.invoices.join(', ')} → Zoho {f.zoho_invoice_number || '(pending)'}{f.by ? ' · ' + f.by : ''}</li>
              ))}</ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function NoticeModal({ c, onClose, onSent }) {
  const [contacts, setContacts] = useState(null);
  const [picked, setPicked] = useState({});
  const [level, setLevel] = useState('auto');
  const [note, setNote] = useState('');
  const [previewOn, setPreviewOn] = useState(false);
  const [pv, setPv] = useState(null);
  const [pvBusy, setPvBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let on = true;
    apiFetch('/api/clients/' + c.client_id).then((r) => (r.ok ? r.json() : null)).then((j) => {
      if (!on) return;
      const list = (j && j.client && j.client.contacts) || [];
      setContacts(list);
      const o = {}; list.forEach((x) => { if (hasEmail(x)) o[x.id] = true; }); setPicked(o);
    }).catch(() => { if (on) setContacts([]); });
    return () => { on = false; };
  }, [c.client_id]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const list = contacts || [];
  const ids = list.filter((x) => hasEmail(x) && picked[x.id]).map((x) => x.id);
  const body = { contact_ids: ids, level, note };
  const previewKey = JSON.stringify(body);
  useEffect(() => {
    if (!previewOn) return undefined;
    let dead = false;
    setPvBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await apiFetch('/api/collections/' + c.client_id + '/notice/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: previewKey });
        const j = await readJson(r);
        if (!dead) setPv(r.ok ? j : { error: j.error || 'Could not build the preview.' });
      } catch (e) { if (!dead) setPv({ error: 'Could not reach the server.' }); }
      finally { if (!dead) setPvBusy(false); }
    }, 350);
    return () => { dead = true; clearTimeout(t); };
  }, [previewOn, previewKey, c.client_id]);

  async function send() {
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/api/collections/' + c.client_id + '/notice', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await readJson(r);
      if (r.ok) { onSent(c, j); return; }
      setErr(j.error || 'Could not send the notice.');
    } catch (e) { setErr('Could not reach the server.'); }
    finally { setBusy(false); }
  }

  const eff = level === 'auto' ? c.level : level;
  return (
    <div className="hq-modal-backdrop" role="dialog" aria-modal="true" aria-label="Send past-due notice"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="hq-modal" style={{ maxWidth: 720 }}>
        <div className="hq-modal-head">
          <h2>Past-due notice — {c.client_name}</h2>
          <button type="button" className="hq-modal-x" aria-label="Close" disabled={busy} onClick={onClose}>×</button>
        </div>
        <div className="hq-modal-body">
          <p className="hq-modal-hint" style={{ marginTop: 0 }}>
            {c.count} past-due invoice{c.count === 1 ? '' : 's'} · {money(c.overdue_total, c.currency_code)} · oldest {c.oldest_days} days.
            {c.last_notice ? ` Last notice ${stamp(c.last_notice.at)} (${LEVEL_LABEL[c.last_notice.level] || c.last_notice.level}).` : ''}
          </p>

          <span className="hq-modal-label">Wording</span>
          <select className="hq-select" value={level} disabled={busy} onChange={(e) => setLevel(e.target.value)}>
            <option value="auto">Automatic — {LEVEL_LABEL[c.level]} (oldest is {c.oldest_days} days)</option>
            <option value="reminder">Reminder</option>
            <option value="late_fee">Late-fee notice</option>
            <option value="collections">Collections warning</option>
          </select>
          <p className="hq-modal-hint">{LEVEL_HELP[eff]} Every notice lists the past-due invoices and quotes your Late Payments terms.</p>

          <span className="hq-modal-label">Send to</span>
          {contacts === null ? <p className="hq-v123-muted">Loading contacts…</p> : list.length === 0 ? (
            <p className="hq-v123-muted">No contacts are attached to this client yet. Add one on the client’s Contacts tab first.</p>
          ) : list.map((x) => {
            const ok = hasEmail(x);
            return (
              <label key={x.id} className="hq-v123-check" style={{ opacity: ok ? 1 : 0.5 }}>
                <input type="checkbox" disabled={!ok || busy} checked={!!picked[x.id] && ok} onChange={() => setPicked((p) => ({ ...p, [x.id]: !p[x.id] }))} />
                <span><b>{nameOf(x)}</b><span className="hq-v123-muted"> · {ok ? x.email : 'no email address'}</span></span>
              </label>
            );
          })}

          <span className="hq-modal-label">Add a note (optional)</span>
          <textarea className="hq-input" rows={3} style={{ width: '100%', resize: 'vertical', height: 'auto' }} maxLength={NOTE_MAX} disabled={busy}
            placeholder="Shown near the top of the email, e.g. “Happy to set up a payment plan — just reply.”"
            value={note} onChange={(e) => setNote(e.target.value)} />
          <p className="hq-modal-hint">{note.length}/{NOTE_MAX} · plain text · from XYZ Merchandise HQ · replies go to info@xyzmerch.com</p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
            <button type="button" className="hq-btn hq-btn-ghost" disabled={busy} onClick={() => setPreviewOn((v) => !v)}>{previewOn ? 'Hide preview' : 'Preview email'}</button>
            {previewOn && pvBusy && <span className="hq-v123-muted">Updating…</span>}
          </div>
          {previewOn && pv && pv.error && <p className="hq-v123-err">{pv.error}</p>}
          {previewOn && pv && !pv.error && (
            <div style={{ marginTop: 10 }}>
              <div className="hq-v123-muted hq-v123-small" style={{ marginBottom: 6, lineHeight: 1.6 }}>
                <div><b>From:</b> {pv.from}</div>
                <div><b>Reply-To:</b> {pv.reply_to}</div>
                <div><b>Subject:</b> {pv.subject}</div>
                <div>{pv.sample_name ? `Previewing as ${pv.sample_name}` : 'Previewing with a generic greeting'}</div>
              </div>
              <iframe title="Email preview" sandbox="" srcDoc={wrapDoc(pv.html)}
                style={{ width: '100%', height: 460, border: '1px solid var(--hq-line)', borderRadius: 8, background: '#ffffff', opacity: pvBusy ? 0.6 : 1 }} />
            </div>
          )}
          {err && <p className="hq-v123-err">{err}</p>}
        </div>
        <div className="hq-modal-foot">
          <button type="button" className="hq-btn hq-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="hq-btn" disabled={busy || ids.length === 0} onClick={send}>
            {busy ? 'Sending…' : `Send to ${ids.length} ${ids.length === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChargeModal({ c, onClose, onDone }) {
  const [pv, setPv] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const take = (j) => { setPv(j); const o = {}; (j.lines || []).forEach((l) => { o[l.number] = true; }); setSel(o); };
  useEffect(() => {
    let on = true;
    apiFetch('/api/collections/' + c.client_id + '/finance-charge/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(async (r) => { const j = await readJson(r); if (!on) return; if (r.ok) take(j); else setPv({ error: j.error || 'Could not load the charge preview.' }); })
      .catch(() => { if (on) setPv({ error: 'Could not reach the server.' }); });
    return () => { on = false; };
  }, [c.client_id]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const lines = (pv && pv.lines) || [];
  const chosen = lines.filter((l) => sel[l.number]);
  const total = Math.round(chosen.reduce((a, l) => a + l.amount, 0) * 100) / 100;
  const skippedCharged = ((pv && pv.skipped) || []).filter((s) => s.reason === 'already_charged');

  async function create() {
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/api/collections/' + c.client_id + '/finance-charge', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numbers: chosen.map((l) => l.number), expected_total: total }),
      });
      const j = await readJson(r);
      if (r.ok) { onDone(c, j); return; }
      if (r.status === 409 && j.preview) take(j.preview);
      setErr(j.error || 'Could not create the finance charge.');
    } catch (e) { setErr('Could not reach the server.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="hq-modal-backdrop" role="dialog" aria-modal="true" aria-label="Create finance charge"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="hq-modal" style={{ maxWidth: 640 }}>
        <div className="hq-modal-head">
          <h2>Finance charge — {c.client_name}</h2>
          <button type="button" className="hq-modal-x" aria-label="Close" disabled={busy} onClick={onClose}>×</button>
        </div>
        <div className="hq-modal-body">
          {!pv ? <p className="hq-v123-muted">Checking balances in Zoho…</p> : pv.error ? <p className="hq-v123-err">{pv.error}</p> : (
            <>
              <p className="hq-modal-hint" style={{ marginTop: 0 }}>
                {pv.rate_pct}% of the balance on each invoice more than 30 days past due, for {pv.month_label}. Creates a <b>draft</b> invoice in Zoho — review and send it from Zoho.
              </p>
              {lines.length === 0 ? <p className="hq-v123-muted">Nothing to charge this month.</p> : (
                <table className="hq-v123-fc">
                  <thead><tr><th /><th>Invoice #</th><th className="r">Days</th><th className="r">Balance</th><th className="r">Charge</th></tr></thead>
                  <tbody>
                    {lines.map((l) => (
                      <tr key={l.number}>
                        <td><input type="checkbox" disabled={busy} checked={!!sel[l.number]} onChange={() => setSel((s) => ({ ...s, [l.number]: !s[l.number] }))} /></td>
                        <td>{l.number}</td>
                        <td className="r">{l.days_past_due}</td>
                        <td className="r">{money(l.balance)}</td>
                        <td className="r">{money(l.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr><td colSpan={4} className="r"><b>Total</b></td><td className="r"><b>{money(total)}</b></td></tr></tfoot>
                </table>
              )}
              {skippedCharged.length > 0 && (
                <p className="hq-modal-hint">Already charged for {pv.month_label}: {skippedCharged.map((s) => s.number).join(', ')}.</p>
              )}
            </>
          )}
          {err && <p className="hq-v123-err">{err}</p>}
        </div>
        <div className="hq-modal-foot">
          <button type="button" className="hq-btn hq-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="hq-btn" disabled={busy || !chosen.length} onClick={create}>
            {busy ? 'Creating…' : `Create draft invoice${chosen.length ? ' · ' + money(total) : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Collections() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('over30');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);
  const [notice, setNotice] = useState(null);
  const [charge, setCharge] = useState(null);
  const [flash, setFlash] = useState('');

  const load = useCallback(async (fresh) => {
    if (fresh) setBusy(true); else setLoading(true);
    try {
      const r = await apiFetch('/api/collections' + (fresh ? '?fresh=1' : ''));
      const j = await readJson(r);
      setData(r.ok ? j : { available: false, reason: 'error', clients: [] });
    } catch (e) { setData({ available: false, reason: 'error', clients: [] }); }
    finally { setLoading(false); setBusy(false); }
  }, []);
  useEffect(() => { load(false); }, [load]);

  const clients = useMemo(() => (data && data.clients) || [], [data]);
  const counts = useMemo(() => {
    const o = {}; for (const f of FILTERS) o[f.k] = clients.filter((c) => matches(c, f.k)).length; return o;
  }, [clients]);
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return clients.filter((c) => matches(c, filter)).filter((c) => !s
      || [c.client_name, c.customer_name, ...c.invoices.map((i) => i.number)].some((v) => v && String(v).toLowerCase().includes(s)));
  }, [clients, filter, search]);
  const shownTotal = useMemo(() => shown.reduce((a, c) => a + (Number(c.overdue_total) || 0), 0), [shown]);

  function onSent(c, j) {
    setNotice(null);
    setFlash(j.sent === j.recipients
      ? `Notice sent to ${j.sent} ${j.sent === 1 ? 'person' : 'people'} for ${c.client_name}.`
      : `${c.client_name}: ${j.sent} of ${j.recipients} emails accepted — check the mail settings (RESEND_API_KEY).`);
    if (j.notice) setData((d) => ({ ...d, clients: d.clients.map((x) => (x.key === c.key ? { ...x, last_notice: { ...j.notice, by: null } } : x)) }));
  }
  function onCharged(c, j) {
    setCharge(null);
    setFlash(`Draft invoice ${j.invoice_number || ''} created in Zoho for ${c.client_name} · ${money(j.total)}. Review and send it from Zoho.`);
    setData((d) => ({ ...d, clients: d.clients.map((x) => (x.key === c.key ? { ...x, charged_this_month: x.charged_this_month + j.lines.length, chargeable: Math.max(0, x.chargeable - j.lines.length) } : x)) }));
  }

  const t = data && data.totals;
  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Collections</h1>
        <p>Clients with past-due invoices in Zoho Books. Send a past-due notice, or add this month’s 1.5% finance charge on balances more than 30 days past due.</p>
      </div>

      {data && data.available && t && (
        <div className="hq-v123-stats">
          <div className="hq-stat"><span className="hq-k">Over 30 days</span><div className="hq-v123-big">{t.over30_clients} client{t.over30_clients === 1 ? '' : 's'}</div><span className="hq-v123-muted">{money(t.over30_total)} more than 30 days past due</span></div>
          <div className="hq-stat"><span className="hq-k">Over 90 days</span><div className="hq-v123-big">{t.over90_clients}</div><span className="hq-v123-muted">eligible for collections</span></div>
          <div className="hq-stat"><span className="hq-k">All past due</span><div className="hq-v123-big">{money(t.past_due_total)}</div><span className="hq-v123-muted">{t.past_due_clients} client{t.past_due_clients === 1 ? '' : 's'}</span></div>
        </div>
      )}

      <div className="hq-panel">
        <div className="hq-panel-head">
          <h2>Past due{data && data.available ? <span className="hq-v123-count">{shown.length} · {money(shownTotal)}</span> : null}</h2>
          <div className="hq-v123-headacts">
            {data && data.fetched_at && <span className="hq-v123-muted hq-v123-small">Updated {stamp(data.fetched_at)}</span>}
            <button className="hq-btn hq-btn-ghost" onClick={() => load(true)} disabled={busy || loading}>{busy ? 'Syncing…' : 'Refresh from Zoho'}</button>
          </div>
        </div>
        {flash && (
          <div className="hq-v123-flash"><span>{flash}</span>
            <button type="button" className="hq-modal-x" onClick={() => setFlash('')} aria-label="Dismiss">×</button></div>
        )}
        {data && data.available && data.migration_ready === false && (
          <p className="hq-v123-warn">The collections migration isn’t applied yet — notices and finance charges are disabled until it is.</p>
        )}
        <div className="hq-v101-toolbar">
          <div className="hq-v101-seg hq-v123-seg">
            {FILTERS.map((f) => (
              <button key={f.k} type="button" className={filter === f.k ? 'on' : ''} onClick={() => setFilter(f.k)}>
                {f.label}{data && data.available ? ' (' + counts[f.k] + ')' : ''}
              </button>
            ))}
          </div>
          <input className="hq-input hq-v123-search" type="search" placeholder="Search client or invoice #…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {data && data.truncated && <p className="hq-v123-warn">Zoho returned more unpaid invoices than HQ reads at once — some may be missing.</p>}

        {loading ? <p className="hq-empty">Loading past-due invoices from Zoho…</p>
          : data && !data.available ? (
            <p className="hq-empty">{data.reason === 'unconfigured' ? 'Zoho is not configured on this server.' : 'Could not reach Zoho right now. Try “Refresh from Zoho” in a minute.'}</p>
          ) : (
            <div className="hq-v123-scroll">
              <table className="hq-table hq-v123-table">
                <thead>
                  <tr>
                    <th>Client</th><th className="r hq-v123-hide-sm">Invoices</th><th className="r">Past due</th>
                    <th className="r">Oldest</th><th>Stage</th><th className="hq-v123-hide-md">Last notice</th><th className="hq-v123-actcol" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((c) => (
                    <React.Fragment key={c.key}>
                      <tr className={'hq-v123-row' + (open === c.key ? ' is-open' : '')} onClick={() => setOpen(open === c.key ? null : c.key)}>
                        <td>
                          <span className="hq-v123-caret" aria-hidden="true">▸</span>
                          {c.client_id
                            ? <Link to={'/clients/' + c.client_id} onClick={(e) => e.stopPropagation()}>{c.client_name}</Link>
                            : <span>{c.customer_name || 'Unknown customer'} <span className="hq-v123-muted">· not linked</span></span>}
                          {c.client_id && c.customer_name && c.customer_name !== c.client_name && <div className="hq-v123-muted hq-v123-small">{c.customer_name}</div>}
                        </td>
                        <td className="r hq-v123-hide-sm">{c.count}</td>
                        <td className="r hq-v123-amt">{money(c.overdue_total, c.currency_code)}</td>
                        <td className={'r' + (c.oldest_days > 90 ? ' hq-v123-red' : c.oldest_days > 30 ? ' hq-v123-amber' : '')}>{c.oldest_days} days</td>
                        <td><Stage level={c.level} />{c.charged_this_month > 0 && <div className="hq-v123-muted hq-v123-small">charged {data.month}</div>}</td>
                        <td className="hq-v123-hide-md">{c.last_notice
                          ? <span className="hq-v123-small">{LEVEL_LABEL[c.last_notice.level] || c.last_notice.level}<br /><span className="hq-v123-muted">{stamp(c.last_notice.at)}</span></span>
                          : <span className="hq-v123-muted">—</span>}</td>
                        <td className="hq-v123-actcol" onClick={(e) => e.stopPropagation()}>
                          {c.client_id && data.migration_ready !== false && (
                            <div className="hq-v123-acts">
                              <button type="button" className="hq-btn hq-v123-go" onClick={() => setNotice(c)}>Send notice</button>
                              {c.chargeable > 0 && <button type="button" className="hq-btn hq-btn-ghost" onClick={() => setCharge(c)}>Finance charge</button>}
                            </div>
                          )}
                        </td>
                      </tr>
                      {open === c.key && (
                        <tr className="hq-v123-detailrow"><td colSpan={7}><Detail c={c} /></td></tr>
                      )}
                    </React.Fragment>
                  ))}
                  {shown.length === 0 && (
                    <tr><td className="hq-empty" colSpan={7}>{clients.length ? 'No clients match.' : 'Nothing is past due.'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
      </div>
      <PortalPayments /> {/* @@PORTAL_PAY_v124@@ */}
      {notice && <NoticeModal c={notice} onClose={() => setNotice(null)} onSent={onSent} />}
      {charge && <ChargeModal c={charge} onClose={() => setCharge(null)} onDone={onCharged} />}
    </div>
  );
}
